import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Briefcase, Shield, Search, Lock, Mail, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function BusinessSecurity() {
  return <Layout data-id="element-4992">
      <div className="container mx-auto px-4 py-12" data-id="element-4993">
        {/* Hero */}
        <div className="mb-12" data-id="element-4994">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-500/10 text-teal-500 text-xs font-medium mb-4 border border-teal-500/20" data-id="element-4995">
            <Briefcase className="h-3 w-3 mr-2" data-id="element-4996" />
            Tools Library
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4997">
            Business Security Tools
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4998">
            Essential security tools for organizations and teams. Manual
            processes don't scale; use these tools to automate protection and
            compliance.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4999">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-5000">
            {/* Why This Matters */}
            <section data-id="element-5001">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-5002">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-teal-500 p-6 rounded-r-lg" data-id="element-5003">
                <p className="text-slate-300 mb-4" data-id="element-5004">
                  Organizations face sophisticated threats that require
                  automated defense. The right tools provide visibility,
                  protection, and response capabilities that manual efforts
                  cannot match.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-5005">
                  <li className="flex items-start" data-id="element-5006">
                    <span className="text-teal-500 mr-2" data-id="element-5007">•</span> Compliance
                    frameworks (ISO 27001, NIST) mandate specific technical
                    controls
                  </li>
                  <li className="flex items-start" data-id="element-5008">
                    <span className="text-teal-500 mr-2" data-id="element-5009">•</span> ROI on
                    security tools is high when they prevent costly breaches
                  </li>
                  <li className="flex items-start" data-id="element-5010">
                    <span className="text-teal-500 mr-2" data-id="element-5011">•</span> Centralized
                    management is essential for scaling security
                  </li>
                </ul>
              </div>
            </section>

            {/* Tool Categories */}
            <section data-id="element-5012">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5013">
                Recommended Tools
              </h2>
              <div className="grid gap-6" data-id="element-5014">
                <ToolCategory title="Endpoint Protection (EDR)" icon={Shield} desc="Next-gen antivirus that detects and responds to threats." tools={[{
                name: 'CrowdStrike Falcon',
                note: 'Cloud-native, AI-powered'
              }, {
                name: 'SentinelOne',
                note: 'Autonomous response, rollback'
              }, {
                name: 'Microsoft Defender',
                note: 'Integrated with Windows'
              }]} safety="Ensure proper configuration and monitoring—tools are only as good as their settings." data-id="element-5015" />
                <ToolCategory title="SIEM & Logging" icon={Search} desc="Centralized log management and threat detection." tools={[{
                name: 'Splunk',
                note: 'Powerful, enterprise-grade'
              }, {
                name: 'Elastic Stack (ELK)',
                note: 'Open source, scalable'
              }, {
                name: 'Microsoft Sentinel',
                note: 'Cloud-native SIEM'
              }]} safety="Define clear use cases to avoid alert fatigue." data-id="element-5016" />
                <ToolCategory title="Vulnerability Scanning" icon={AlertTriangle} desc="Identify weaknesses in your infrastructure before attackers do." tools={[{
                name: 'Nessus',
                note: 'Industry standard scanner'
              }, {
                name: 'OpenVAS',
                note: 'Open source alternative'
              }, {
                name: 'Qualys',
                note: 'Cloud-based monitoring'
              }]} safety="Scan regularly and prioritize critical vulnerabilities for patching." data-id="element-5017" />
                <ToolCategory title="Email Security" icon={Mail} desc="Protect against phishing, spam, and malware." tools={[{
                name: 'Proofpoint',
                note: 'Advanced threat protection'
              }, {
                name: 'Mimecast',
                note: 'Archiving and continuity'
              }, {
                name: 'Defender for Office 365',
                note: 'Integrated protection'
              }]} safety="Configure SPF, DKIM, and DMARC to prevent domain spoofing." data-id="element-5018" />
              </div>
            </section>

            {/* Implementation Guide */}
            <section data-id="element-5019">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5020">
                Implementation Guide
              </h2>
              <div className="space-y-4" data-id="element-5021">
                <Step number={1} text="Deploy Endpoint Protection (EDR) on all workstations and servers." data-id="element-5022" />
                <Step number={2} text="Implement centralized logging (SIEM) for critical systems." data-id="element-5023" />
                <Step number={3} text="Set up monthly vulnerability scanning." data-id="element-5024" />
                <Step number={4} text="Deploy a business password manager (1Password/Bitwarden)." data-id="element-5025" />
                <Step number={5} text="Configure email security protocols (SPF/DKIM/DMARC)." data-id="element-5026" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-5027">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-5028">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-5029">
                <ul className="space-y-3 text-slate-300" data-id="element-5030">
                  <li className="flex items-start" data-id="element-5031">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5032" />
                    Deploying tools without proper configuration (using
                    defaults).
                  </li>
                  <li className="flex items-start" data-id="element-5033">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5034" />
                    Buying tools without a plan for who will manage them.
                  </li>
                  <li className="flex items-start" data-id="element-5035">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-5036" />
                    Ignoring alerts due to volume (alert fatigue).
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-5037">
            <Card className="p-6 sticky top-24" data-id="element-5038">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-5039">
                Quick Actions
              </h3>
              <Link to="/organization/protect" className="block w-full" data-id="element-5040">
                <Button className="w-full bg-teal-500 hover:bg-teal-600 text-slate-950" data-id="element-5041">
                  Protection Protocol <ArrowRight className="ml-2 h-4 w-4" data-id="element-5042" />
                </Button>
              </Link>
              <Link to="/organization/detect" className="block w-full mt-3" data-id="element-5043">
                <Button variant="secondary" className="w-full" data-id="element-5044">
                  Detection Protocol
                </Button>
              </Link>

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-5045">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-5046">
                  Related
                </h4>
                <ul className="space-y-2 text-sm text-slate-400" data-id="element-5047">
                  <li data-id="element-5048">
                    <Link to="/government/vendor-security" className="hover:text-teal-500" data-id="element-5049">
                      Vendor Security
                    </Link>
                  </li>
                  <li data-id="element-5050">
                    <Link to="/compliance" className="hover:text-teal-500" data-id="element-5051">
                      Compliance Guides
                    </Link>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ToolCategory({
  title,
  icon: Icon,
  desc,
  tools,
  safety
}: {
  title: string;
  icon: any;
  desc: string;
  tools: {
    name: string;
    note: string;
  }[];
  safety: string;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800" data-id="element-5052">
      <div className="flex items-start mb-4" data-id="element-5053">
        <div className="bg-teal-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-5054">
          <Icon className="h-6 w-6 text-teal-500" data-id="element-5055" />
        </div>
        <div data-id="element-5056">
          <h3 className="text-lg font-bold text-slate-200 mb-1" data-id="element-5057">{title}</h3>
          <p className="text-slate-400 text-sm" data-id="element-5058">{desc}</p>
        </div>
      </div>

      <div className="space-y-3 mb-6 pl-16" data-id="element-5059">
        {tools.map((tool, idx) => <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 last:border-0 last:pb-0" data-id="element-5060">
            <span className="font-bold text-slate-300" data-id="element-5061">{tool.name}</span>
            <span className="text-slate-500" data-id="element-5062">{tool.note}</span>
          </div>)}
      </div>

      <div className="bg-slate-800/50 p-3 rounded text-xs text-slate-400 flex items-start" data-id="element-5063">
        <Shield className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" data-id="element-5064" />
        <span data-id="element-5065">
          <strong data-id="element-5066">Safety Note:</strong> {safety}
        </span>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-5067">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-teal-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-5068">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-5069">{text}</p>
    </div>;
}